/**
 * GameInterface utilities for independent game development
 * 
 * This module provides minimal ABIs and utilities to interact with GameInterface-compliant
 * game contracts without requiring individual game ABI imports.
 */

import { useState, useEffect } from 'react';
import { createContract } from './EthersSetup.js';

// Global singleton cache for GameRegistry data
const gameRegistryCache = {
  addresses: null,
  loading: false,
  error: null,
  lastFetchKey: null,
  subscribers: new Set()
};

// Helper to create cache key for deduplication
const createCacheKey = (provider, GameRegistryConfig, gameRegistryAddress) => {
  return `${provider ? 'provider' : 'no-provider'}_${GameRegistryConfig ? 'config' : 'no-config'}_${gameRegistryAddress || 'no-address'}`;
};

// Minimal ABI containing only the standardized GameInterface events
// This allows event listening without importing individual game ABIs
export const GAME_INTERFACE_EVENTS_ABI = [
  // Standard GameInterface events used by most games
  "event GameFinished(uint indexed gameId, address winner)",
  "event GameStarted(uint indexed gameId, address player1, address player2)",
  "event GameKilled(uint indexed gameId, uint refund_amount_player1, uint refund_amount_player2, uint remainder)",
  
  // Risk variant (uses GameEnded instead of GameFinished)
  "event GameEnded(uint indexed gameId, address winner)",
  
  // Mill variant (has additional firstPlayer parameter)
  "event GameStarted(uint indexed gameId, address player1, address player2, address firstPlayer)",
  
  // Game move events - for triggering PersonalizedSection updates
  "event MoveMade(uint indexed gameId, address player, uint position)",  // TicTacToe
  "event TurnSubmitted(uint indexed gameId, address player)",             // Risk
  "event ChoiceMade(uint indexed gameId, address player)",                // RockPaperScissors  
  "event ChoiceRevealed(uint indexed gameId, address player)",            // RockPaperScissors
  "event PiecePlaced(uint indexed gameId, address player, uint position)", // Mill
  "event PieceMoved(uint indexed gameId, address player, uint from, uint to)" // Mill
];

/**
 * Singleton function to fetch game addresses (only fetch once globally)
 */
const fetchGameAddressesSingleton = async (provider, GameRegistryConfig, gameRegistryAddress) => {
  const cacheKey = createCacheKey(provider, GameRegistryConfig, gameRegistryAddress);

  // If we're already loading the same data, wait for it
  if (gameRegistryCache.loading && gameRegistryCache.lastFetchKey === cacheKey) {
    return new Promise((resolve) => {
      const checkComplete = () => {
        if (!gameRegistryCache.loading) {
          resolve({
            addresses: gameRegistryCache.addresses,
            error: gameRegistryCache.error
          });
        } else {
          setTimeout(checkComplete, 100);
        }
      };
      checkComplete();
    });
  }

  // If we already have this data cached, return it
  if (gameRegistryCache.addresses && gameRegistryCache.lastFetchKey === cacheKey) {
    return {
      addresses: gameRegistryCache.addresses,
      error: gameRegistryCache.error
    };
  }

  // Start fresh fetch
  gameRegistryCache.loading = true;
  gameRegistryCache.error = null;
  gameRegistryCache.lastFetchKey = cacheKey;

  try {
    console.log('🔍 Fetching game addresses from GameRegistry (singleton)...');

    const gameRegistry = await createContract(gameRegistryAddress, GameRegistryConfig.abi, false);

    // Handle case when wallet not connected (returns null)
    if (!gameRegistry) {
      console.log('⏳ Wallet not connected - deferring game addresses loading');
      return { addresses: null, error: 'Connect your wallet to view available games' };
    }

    const gameNames = await gameRegistry.getAllGameNames();

    const addresses = {};
    for (const gameName of gameNames) {
      const gameInfo = await gameRegistry.getGameInfo(gameName);
      addresses[gameName] = gameInfo.gameAddress;
      console.log(`✅ ${gameName}: ${gameInfo.gameAddress}`);
    }

    gameRegistryCache.addresses = addresses;
    console.log(`✅ Loaded ${Object.keys(addresses).length} game addresses from GameRegistry (singleton)`);

    return { addresses, error: null };

  } catch (err) {
    console.error('❌ Error fetching game addresses:', err);

    // Provide more specific error messages for common issues
    let errorMessage = err.message;
    if (err.message.includes('GameRegistry contract not available')) {
      errorMessage = 'Network mismatch: GameRegistry contract not found on current network. Please check your wallet network settings.';
    } else if (err.message.includes('call revert exception')) {
      errorMessage = 'Contract call failed: Unable to connect to GameRegistry. Please check your network connection and try again.';
    } else if (err.message.includes('provider')) {
      errorMessage = 'Connection error: Unable to connect to blockchain. Please refresh the page or check your wallet connection.';
    }

    const error = `Failed to fetch game addresses: ${errorMessage}`;
    gameRegistryCache.error = error;
    return { addresses: null, error };
  } finally {
    gameRegistryCache.loading = false;
    // Notify all subscribers
    gameRegistryCache.subscribers.forEach(callback => callback());
  }
};

/**
 * Custom hook to get game contract addresses from GameRegistry with singleton pattern
 * Prevents multiple simultaneous fetches of the same GameRegistry data
 *
 * @param {Object} provider - Ethers provider
 * @param {Object} GameRegistryConfig - GameRegistry contract ABI
 * @param {string} gameRegistryAddress - GameRegistry contract address
 * @returns {Object} { gameAddresses, loading, error }
 */
export const useGameAddresses = (provider, GameRegistryConfig, gameRegistryAddress) => {
  const [gameAddresses, setGameAddresses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {

    if (!provider || !GameRegistryConfig || !gameRegistryAddress) {
      console.log('⏳ DEBUG: useGameAddresses - missing dependencies, setting loading false');
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadData = async () => {
      const result = await fetchGameAddressesSingleton(provider, GameRegistryConfig, gameRegistryAddress);

      if (mounted) {
        if (result.addresses) {
          setGameAddresses(result.addresses);
          setError(null);
        } else {
          setError(result.error);
        }
        setLoading(false);
      }
    };

    // Subscribe to cache updates
    const updateCallback = () => {
      if (mounted && !gameRegistryCache.loading) {
        if (gameRegistryCache.addresses) {
          setGameAddresses(gameRegistryCache.addresses);
          setError(null);
        } else {
          setError(gameRegistryCache.error);
        }
        setLoading(false);
      }
    };

    gameRegistryCache.subscribers.add(updateCallback);
    loadData();

    return () => {
      mounted = false;
      gameRegistryCache.subscribers.delete(updateCallback);
    };
  }, [provider, GameRegistryConfig, gameRegistryAddress]);

  return { gameAddresses, loading, error };
};

/**
 * Utility function to create a contract instance with minimal GameInterface events ABI
 * 
 * @param {string} contractAddress - Contract address
 * @param {boolean} withSigner - Whether to create contract with signer (for transactions)
 * @returns {Object} Ethers contract instance
 */
export const createGameInterfaceContract = async (contractAddress, withSigner = false) => {
  if (!contractAddress || contractAddress === '0x0000000000000000000000000000000000000000') {
    return null;
  }

  return await createContract(contractAddress, GAME_INTERFACE_EVENTS_ABI, withSigner);
};

/**
 * Helper function to set up event listeners for both GameFinished and GameEnded events
 * Handles the variation between most games (GameFinished) and Risk games (GameEnded)
 * 
 * @param {Object} contract - Ethers contract instance
 * @param {number} gameId - Specific game ID to filter events (optional)
 * @param {Function} handler - Event handler function
 * @returns {Function} Cleanup function to remove event listeners
 */
export const setupGameCompletionListeners = (contract, gameId, handler) => {
  if (!contract || !contract.filters) return () => {};

  try {
    const gameFinishedFilter = gameId
      ? contract.filters.GameFinished(gameId)
      : contract.filters.GameFinished();

    const gameEndedFilter = gameId
      ? contract.filters.GameEnded(gameId)
      : contract.filters.GameEnded();

    contract.on(gameFinishedFilter, handler);
    contract.on(gameEndedFilter, handler);

    console.log(`🎧 Set up completion listeners for contract ${contract.address}${gameId ? ` (gameId: ${gameId})` : ''}`);

    // Return cleanup function
    return () => {
      try {
        contract.off(gameFinishedFilter, handler);
        contract.off(gameEndedFilter, handler);
        console.log(`🧹 Cleaned up completion listeners for contract ${contract.address}${gameId ? ` (gameId: ${gameId})` : ''}`);
      } catch (error) {
        console.warn(`⚠️ Error cleaning up listeners for contract ${contract.address}:`, error);
      }
    };
  } catch (error) {
    console.error(`❌ Error setting up completion listeners for contract:`, error);
    return () => {};
  }
};

/**
 * Helper function to set up event listeners for game move events
 * Listens for events that indicate a player has made a move (changes game state)
 * 
 * @param {Object} contract - Ethers contract instance
 * @param {number} gameId - Specific game ID to filter events (optional)
 * @param {Function} handler - Event handler function
 * @returns {Function} Cleanup function to remove event listeners
 */
export const setupGameMoveListeners = (contract, gameId, handler) => {
  if (!contract || !contract.filters) return () => {};

  const listeners = [];
  const moveEvents = ['MoveMade', 'TurnSubmitted', 'ChoiceMade', 'ChoiceRevealed', 'PiecePlaced', 'PieceMoved'];

  moveEvents.forEach(eventName => {
    try {
      const eventFilter = gameId 
        ? contract.filters[eventName](gameId)
        : contract.filters[eventName]();
      
      contract.on(eventFilter, handler);
      listeners.push({ filter: eventFilter, handler });
      console.log(`🎧 Set up ${eventName} listener for contract ${contract.address}${gameId ? ` (gameId: ${gameId})` : ''}`);
    } catch (error) {
      // Some contracts may not have all move events - this is expected
      console.debug(`${eventName} event not available for contract ${contract.address}`);
    }
  });

  // Return cleanup function
  return () => {
    try {
      listeners.forEach(({ filter, handler }) => {
        contract.off(filter, handler);
      });
      console.log(`🧹 Cleaned up game move listeners for contract ${contract.address}${gameId ? ` (gameId: ${gameId})` : ''}`);
    } catch (error) {
      console.warn(`⚠️ Error cleaning up move listeners for contract ${contract.address}:`, error);
    }
  };
};

/**
 * Helper function to determine the correct game completion event name for a contract
 * Most games use "GameFinished", but Risk games use "GameEnded"
 * 
 * @param {string} gameName - Name of the game
 * @returns {string} Event name to listen for
 */
export const getGameCompletionEventName = (gameName) => {
  // Risk games use GameEnded instead of GameFinished
  if (gameName && gameName.toLowerCase().includes('risk')) {
    return 'GameEnded';
  }
  
  // All other games use the standard GameFinished
  return 'GameFinished';
};